/**
 * Shared, load-once access to the MCP server list (#939, Feature D).
 *
 * The registry is read the first time an MCP page (catalog, server detail, Skills search
 * pointer) asks for it, then reused for the rest of the visit, so moving between MCP pages
 * doesn't refetch. Nothing is fetched for people who never open an MCP page.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { loadMcpServers, type McpServersResult } from "./mcp-registry";
import { reportError } from "./newrelic";

export type McpServersState =
  | { status: "loading" }
  | { status: "ready"; result: McpServersResult }
  | { status: "error" };

let pending: Promise<McpServersResult> | null = null;
// The list once it has loaded, so a page that mounts after that starts ready instead of
// flashing "Loading…" for a frame. A cached (registry down) list is kept for the visit too;
// a recovered registry is picked up on the next visit.
let loaded: McpServersResult | null = null;

function loadOnce(): Promise<McpServersResult> {
  if (!pending) {
    pending = loadMcpServers().then(
      (result) => {
        loaded = result;
        return result;
      },
      (err: unknown) => {
        // Let a later visit to an MCP page retry instead of caching the failure.
        pending = null;
        loaded = null;
        throw err;
      },
    );
  }
  return pending;
}

/** Test-only: forget the shared list so each test starts fresh. */
export function resetMcpServersForTests(): void {
  pending = null;
  loaded = null;
}

/** The list's state, plus `retry` for the error state's Try again button. */
export function useMcpServers(): McpServersState & { retry: () => void } {
  const [state, setState] = useState<McpServersState>(() =>
    loaded ? { status: "ready", result: loaded } : { status: "loading" },
  );
  const [attempt, setAttempt] = useState(0);
  // Try again means fetch again: forget a stored (cached) result too, or the retry would just
  // hand back the same saved list.
  const retry = useCallback(() => {
    pending = null;
    loaded = null;
    setState({ status: "loading" });
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadOnce()
      .then((result) => {
        if (!cancelled) setState({ status: "ready", result });
      })
      .catch((err: unknown) => {
        reportError(err, "mcp-registry-load");
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  return useMemo(() => ({ ...state, retry }), [state, retry]);
}
